import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { get_product_search /*, get_product_search_with_quotation */ } from '../services/order';
import ProductDescribe from './ProductDescribe';
import AddIcon from '@mui/icons-material/Add';
import ProductCreate from '../../product/components/Product';
import { removeWord } from '../constants';

interface ProductOption {
  id: string;
  name: string;            // plain string for internal filtering/label
  node: React.ReactNode;   // styled node shown in dropdown
}

const ProductSearch = ({
  quotationId,
  onSelectToOrder,
  orderProducts,
  companyId,
  setOrderProducts,
  withGst,
  units,
  searchString
}: any) => {
  console.log("searchString", searchString);

  // ✅ Safe, prop-driven initial state (don’t break if undefined)
  const [searchTerm, setSearchTerm] = useState<string>((searchString ?? '').trim());
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>((searchString ?? '').trim());
  const [value, setValue] = useState<ProductOption | null>(null);
  const [optionsData, setOptionData] = useState<any[]>([]);
  const [selectedProductDetails, setSelectedProductDetails] = useState<any>();

  // ✅ NEW: control Autocomplete popup visibility (open on focus/typing/prop)
  const [open, setOpen] = useState(false);

  const fetchProducts = useCallback(
    debounce(async (query: string) => {
      if (query.length >= 1) {
        setLoading(true);
        try {
          let response: any = null;

          // if (quotationId) {
          //   response = await get_product_search_with_quotation(query, quotationId, companyId);
          // } else {
          response = await get_product_search(query, companyId);
          // }

          const rows = response?.data?.data ?? [];
          setOptionData(rows);

          if (rows.length) {
            const mapped: ProductOption[] = rows.map((e: any) => {
              const isSame = e.productName === e.companyProductCode;
              const rawCode = removeWord(e.companyProductCode, e.productName);

              const name = isSame
                ? e.productName
                : `${e.productName} (${rawCode}) (${e.companyName})`;

              const node = isSame ? (
                <span>{e.productName}</span>
              ) : (
                <span>
                  <span>{e.productName}</span>{' '}
                  <span style={{ fontSize: 12, color: '#ff5757' }}>({rawCode})</span>{' '}
                  <span style={{ fontSize: 12, color: 'green' }}>({e.companyName})</span>
                </span>
              );

              return { id: e.id, name, node };
            });

            setOptions(mapped);
          } else {
            setOptions([]);
          }
        } catch (_err) {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }
    }, 300),
    [quotationId, companyId]
  );

  // ✅ Cancel any pending debounced call on unmount
  useEffect(() => {
    return () => (fetchProducts as any).cancel?.();
  }, [fetchProducts]);

  // ✅ Fetch when user types (debounced via fetchProducts)
  useEffect(() => {
    if (searchTerm && searchTerm.length >= 1) {
      fetchProducts(searchTerm);
    }
  }, [searchTerm, fetchProducts]);

  // ✅ Sync with prop: on mount or when parent changes searchString,
  // reflect it in the input AND fetch immediately (don’t wait for debounce)
  useEffect(() => {
    const s = (searchString ?? '').trim();
    setInputValue(s);
    setSearchTerm(s);
    if (s) {
      fetchProducts(s);
      setOpen(true); // open when parent provides a term
    }
  }, [searchString, fetchProducts]);

  // ✅ Keep dropdown open while there’s text; close when cleared
  useEffect(() => {
    setOpen(Boolean(inputValue?.length));
  }, [inputValue]);

  const onChangeProductSearch = (productOption: ProductOption | null) => {
    setInputValue('');
    if (productOption && optionsData.length > 0) {
      const product = optionsData.find((item: any) => item.id === productOption.id);
      if (product?.product_attributes?.length) {
        setSelectedProductDetails(product);
      }
    }
  };

  useEffect(() => {
    if (orderProducts.length === 0) {
      setSelectedProductDetails(undefined);
      setInputValue('');
    }
  }, [orderProducts.length]);

  // Reset suggestions & selection when companyId changes
  useEffect(() => {
    setOptions([]);
    setOptionData([]);
    setSelectedProductDetails(undefined);
    setInputValue('');
    setSearchTerm('');
    setValue(null);
  }, [companyId]);

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex' }}>
        <div style={{ width: '95%' }}>
          <Autocomplete<ProductOption, false, false, false>
            options={options}
            getOptionLabel={(option) => option.name}
            loading={loading}
            size="small"
            inputValue={inputValue}
            value={value}
            // ✅ control popup + open on focus
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            openOnFocus
            isOptionEqualToValue={(option, v) => option.id === v.id}
            onInputChange={(_event, newInputValue) => {
              setInputValue(newInputValue);
              setSearchTerm(newInputValue);
            }}
            onChange={(_event, newValue) => {
              onChangeProductSearch(newValue);
              setValue(null);       // clear selection after pick
              setInputValue('');    // clear input
              setSearchTerm('');    // stop fetching
              // effect on inputValue will close popup automatically
            }}
            // 3-columns grid in dropdown listbox
            ListboxProps={{
              style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 8,
                padding: 8,
              },
            }}
            renderOption={(props, option) => {
              // Merge styles into the <li> so it looks like a tile
              const mergedStyle = {
                ...(props.style || {}),
                margin: 0,
                padding: 8,
                borderRadius: 6,
                whiteSpace: 'normal' as const,
                alignItems: 'stretch',
              };
              return (
                <li {...props} style={mergedStyle}>
                  <div style={{ width: '100%' }}>{option.node}</div>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Product"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText="No products found"
          />
        </div>
        <div style={{ width: '5%' }}>
          <IconButton onClick={handleDialogOpen} aria-label="add" sx={{ marginLeft: 2 }}>
            <AddIcon />
          </IconButton>
        </div>
      </div>

      <div style={{ width: '100%' }}>
        <ProductDescribe
          product={selectedProductDetails}
          onSelectToOrder={onSelectToOrder}
          orderProducts={orderProducts}
        />
      </div>

      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="lg">
        <DialogTitle>Create New Product</DialogTitle>
        <DialogContent>
          <ProductCreate
            setSelectedProductDetails={setSelectedProductDetails}
            setDialogOpen={setDialogOpen}
            quotationId={quotationId}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductSearch;
